import { useCallback, useEffect, useRef, useState } from 'react';
import { useSubjectLanguageVariants } from '@features/user';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import {
  useCreateRequirementDefinitionMutation,
  useDefinitionMaterialization,
  useRequirementCatalog,
  useUpdateRequirementDefinitionMutation,
} from '../store';
import type { DefinitionCreateRequest } from '../types';

import { BulkLaunchRow } from './BulkLaunchRow';
import { CreateRequirementDialog } from './CreateRequirementDialog';
import { PatientRequirementsCatalogView } from './PatientRequirementsCatalogView';
import { RequestCopySection } from './RequestCopySection';

/**
 * Org settings entry point for the requirement catalog (GDN-3224, AC-1).
 *
 * The caller owns the gate and the permission check; this component assumes it
 * should render.
 */
export const PatientRequirementsSection = () => {
  const subject = useSubjectLanguageVariants();
  const { enqueueSnackbar } = useSnackbar();
  const { patientRequirementsCatalog } = useEventTracking();

  const {
    organizationId,
    definitions,
    isLoading,
    isRefreshing,
    isError,
    refetch,
    availableKinds,
  } = useRequirementCatalog({
    // A run started from this page finishes on its own, and nothing here would
    // ever say so without the poll: the row would sit on "Checking existing
    // records…" until a reload (GDN-3589). Drops itself when nothing is
    // checking; the rail card already opts in the same way.
    pollWhileChecking: true,
  });

  const [updateDefinition] = useUpdateRequirementDefinitionMutation();
  const [createDefinition] = useCreateRequirementDefinitionMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [createdDefinitionId, setCreatedDefinitionId] = useState<string | undefined>();

  const { definition: materializing, isChecking } =
    useDefinitionMaterialization(createdDefinitionId);

  // One catalog_opened per mount, once the first load resolves — firing on
  // every render would inflate the count, and firing before load would report
  // counts of zero for a catalog that turned out to be populated.
  const openedTracked = useRef(false);
  useEffect(() => {
    if (openedTracked.current || isLoading || isError) return;
    openedTracked.current = true;
    patientRequirementsCatalog.opened({
      organization_id: organizationId ?? undefined,
      definition_count: definitions.length,
      active_count: definitions.filter(definition => definition.active).length,
    });
  }, [isLoading, isError, definitions, organizationId, patientRequirementsCatalog]);

  const handleUpdate = useCallback(
    async (
      definitionId: string,
      patch: { label?: string; active?: boolean; default_for_new_patients?: boolean },
    ) => {
      if (!organizationId) return;
      const definition = definitions.find(
        candidate => candidate.patient_requirement_definition_id === definitionId,
      );
      try {
        await updateDefinition({
          orgId: organizationId,
          definitionId,
          definitionUpdateRequest: patch,
        }).unwrap();

        if (patch.label !== undefined) {
          patientRequirementsCatalog.labelChanged({
            organization_id: organizationId,
            definition_id: definitionId,
            kind: definition?.kind,
            label_length: patch.label.length,
          });
        }
        if (patch.active !== undefined) {
          patientRequirementsCatalog.activeToggled({
            organization_id: organizationId,
            definition_id: definitionId,
            kind: definition?.kind,
            active: patch.active,
          });
        }
      } catch {
        enqueueSnackbar('Could not save that change. Please try again.', {
          variant: 'error',
        });
        // Rethrow so the row's autosave shows its error state instead of a
        // green "saved" dot for a change the server rejected.
        throw new Error('update-failed');
      }
    },
    [
      organizationId,
      definitions,
      updateDefinition,
      enqueueSnackbar,
      patientRequirementsCatalog,
    ],
  );

  const handleCreate = useCallback(
    async (body: DefinitionCreateRequest) => {
      if (!organizationId) throw new Error('missing-organization');
      const created = await createDefinition({
        orgId: organizationId,
        definitionCreateRequest: body,
      }).unwrap();

      patientRequirementsCatalog.createSubmitted({
        organization_id: organizationId,
        kind: body.kind,
        default_for_new_patients: body.default_for_new_patients,
      });

      setCreatedDefinitionId(created.patient_requirement_definition_id);
      return created;
    },
    [organizationId, createDefinition, patientRequirementsCatalog],
  );

  const handleAddClick = () => {
    patientRequirementsCatalog.createOpened({
      organization_id: organizationId ?? undefined,
      available_kinds_count: availableKinds.length,
    });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    // Stop polling; the catalog list already refetched via tag invalidation.
    setCreatedDefinitionId(undefined);
  };

  return (
    <>
      <PatientRequirementsCatalogView
        definitions={definitions}
        renderBulkLaunch={definition => (
          <BulkLaunchRow
            definitionId={definition.patient_requirement_definition_id}
            requirementLabel={definition.label}
            requirementKind={definition.kind}
            isMaterializing={definition.materialization_status === 'checking'}
          />
        )}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        isError={isError}
        availableKinds={availableKinds}
        subject={subject}
        onUpdateDefinition={handleUpdate}
        onAddClick={handleAddClick}
        onRetry={refetch}
      />

      {/* AC-11's default copy, under the catalog it applies to: one message
          set for every request, single sends from a chart and bulk sends from
          a catalog row alike. The caller owns the gate and the admin check
          for the catalog above; this section repeats them because it is the
          write surface. */}
      <RequestCopySection />

      <CreateRequirementDialog
        open={dialogOpen}
        availableKinds={availableKinds}
        subject={subject}
        onClose={handleDialogClose}
        onCreate={handleCreate}
        materializing={materializing}
        isChecking={isChecking}
      />
    </>
  );
};
